/**
 * ConfigurationModal Component
 *
 * Modal for creating and editing configurations.
 * Uses React state instead of jQuery for modal handling.
 */

var React = require('react');
var useState = React.useState;
var useEffect = React.useEffect;
var useCallback = React.useCallback;
var { useDispatch, useSelector } = require('react-redux');
var { MultiSelect } = require('react-multi-select-component');
var distributionActions = require('distributionActions');
var {
  ENVIRONMENT_OPTIONS,
  VALUE_OPTIONS,
  EMPTY_CONFIGURATION,
} = require('./RemoteConfigurations.types');
var { validateConfiguration } = require('./RemoteConfigurations.util');

/**
 * Build environment options with True/False values
 */
var buildEnvOptions = function () {
  var options = [];
  ENVIRONMENT_OPTIONS.forEach(function (env) {
    options.push({ value: env.value + ' :True', label: env.label + ' :True' });
    options.push({
      value: env.value + ' :False',
      label: env.label + ' :False',
    });
  });
  return options;
};

var CONFIGURATION_ENV_OPTIONS = buildEnvOptions();

/**
 * ConfigurationModal component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Object|null} props.configuration - Configuration to edit, null for new
 * @param {Function} props.onClose - Called when modal is closed
 * @param {Function} props.onSave - Called when save button is clicked
 */
var ConfigurationModal = function (props) {
  var isOpen = props.isOpen;
  var configuration = props.configuration;
  var onClose = props.onClose;
  var onSave = props.onSave;

  var dispatch = useDispatch();
  var all_distributions = useSelector(function (state) {
    return state.distribution_data && state.distribution_data.distributions;
  });

  var formDataState = useState(EMPTY_CONFIGURATION);
  var formData = formDataState[0];
  var setFormData = formDataState[1];

  var errorsState = useState({});
  var errors = errorsState[0];
  var setErrors = errorsState[1];

  var isSubmittingState = useState(false);
  var isSubmitting = isSubmittingState[0];
  var setIsSubmitting = isSubmittingState[1];

  var isEditMode = configuration && configuration.id;

  // Build distribution options
  var distributionOptions = React.useMemo(
    function () {
      if (!all_distributions || !all_distributions.distributions) {
        return [];
      }
      var options = [];
      var sortedDistributions = Object.values(
        all_distributions.distributions,
      ).sort(function (a, b) {
        return (a.name || '').localeCompare(b.name || '');
      });
      sortedDistributions.forEach(function (distribution) {
        options.push({
          value: distribution.name + ' :True',
          label: distribution.name + ' :True',
        });
        options.push({
          value: distribution.name + ' :False',
          label: distribution.name + ' :False',
        });
      });
      return options;
    },
    [all_distributions],
  );

  // Build farming unit options
  var farmingUnitOptions = React.useMemo(
    function () {
      if (!all_distributions || !all_distributions.distributions) {
        return [];
      }
      var farmingUnits = [];
      Object.values(all_distributions.distributions).forEach(
        function (distribution) {
          if (distribution.farming_units) {
            Object.values(distribution.farming_units).forEach(
              function (farmingUnit) {
                farmingUnits.push(farmingUnit);
              },
            );
          }
        },
      );
      farmingUnits.sort(function (a, b) {
        return (a.name || '').localeCompare(b.name || '');
      });
      var options = [];
      farmingUnits.forEach(function (farmingUnit) {
        options.push({
          value: farmingUnit.name + ' :True',
          label: farmingUnit.name + ' :True',
        });
        options.push({
          value: farmingUnit.name + ' :False',
          label: farmingUnit.name + ' :False',
        });
      });
      return options;
    },
    [all_distributions],
  );

  var treesLoadingState = useState(false);
  var treesLoading = treesLoadingState[0];
  var setTreesLoading = treesLoadingState[1];

  // Lazy-load full distribution trees when the modal opens.
  useEffect(
    function () {
      if (!isOpen) {
        return;
      }
      setTreesLoading(true);
      dispatch(distributionActions.ensure_all_distributions_loaded()).finally(
        function () {
          setTreesLoading(false);
        },
      );
    },
    [dispatch, isOpen],
  );

  // Reset form when modal opens
  useEffect(
    function () {
      if (isOpen) {
        if (configuration) {
          // Edit mode - populate form with existing data
          // Handle users_email conversion safely
          var usersEmailStr = '';
          if (Array.isArray(configuration.users_email)) {
            usersEmailStr = configuration.users_email.join(', ');
          } else if (typeof configuration.users_email === 'string') {
            usersEmailStr = configuration.users_email;
          }

          setFormData({
            id: configuration.id || '',
            configuration_name: configuration.configuration_name || '',
            default_value:
              configuration.default_value !== undefined
                ? configuration.default_value
                : false,
            description: configuration.description || '',
            environments: Array.isArray(configuration.environments)
              ? configuration.environments
              : [],
            users_email: usersEmailStr,
            distributions: Array.isArray(configuration.distributions)
              ? configuration.distributions
              : [],
            farming_units: Array.isArray(configuration.farming_units)
              ? configuration.farming_units
              : [],
            extra_data: configuration.extra_data || '',
          });
        } else {
          // Create mode - reset to empty
          setFormData(EMPTY_CONFIGURATION);
        }
        setErrors({});
      }
    },
    [isOpen, configuration],
  );

  var handleInputChange = function (field) {
    return function (e) {
      var value = e.target.value;
      setFormData(function (prev) {
        var updated = Object.assign({}, prev);
        updated[field] = value;
        return updated;
      });
      // Clear error on change
      if (errors[field]) {
        setErrors(function (prev) {
          var updated = Object.assign({}, prev);
          delete updated[field];
          return updated;
        });
      }
    };
  };

  var handleMultiSelectChange = function (field) {
    return function (selected) {
      setFormData(function (prev) {
        var updated = Object.assign({}, prev);
        updated[field] = selected.map(function (item) {
          return item.value;
        });
        return updated;
      });
    };
  };

  var handleClose = function () {
    if (!isSubmitting && onClose) {
      onClose();
    }
  };

  var handleSubmit = function (e) {
    e.preventDefault();

    // Validate
    var validation = validateConfiguration(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);

    // Prepare data for save
    var dataToSave = Object.assign({}, formData);
    if (isEditMode) {
      dataToSave.id = configuration.id;
    }

    if (onSave) {
      // Wrap in Promise.resolve to handle non-promise returns
      Promise.resolve(onSave(dataToSave, isEditMode))
        .then(function () {
          setIsSubmitting(false);
          handleClose();
        })
        .catch(function (err) {
          console.error('Failed to save configuration:', err);
          setIsSubmitting(false);
        });
    } else {
      // Reset submitting state if no save handler
      setIsSubmitting(false);
    }
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Convert arrays to MultiSelect format
  var getMultiSelectValue = function (items) {
    if (!items || !Array.isArray(items)) {
      return [];
    }
    return items.map(function (item) {
      return { value: item, label: item };
    });
  };

  return React.createElement(
    'div',
    {
      className: 'remote-config-modal-overlay',
      onClick: function (e) {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      },
    },
    React.createElement(
      'div',
      {
        className: 'remote-config-modal',
        onClick: function (e) {
          e.stopPropagation();
        },
      },
      [
        // Header
        React.createElement(
          'div',
          {
            key: 'header',
            className: 'remote-config-modal__header',
          },
          [
            React.createElement(
              'h2',
              { key: 'title' },
              isEditMode ? 'Edit Configuration' : 'Add New Configuration',
            ),
            React.createElement(
              'button',
              {
                key: 'close',
                className: 'remote-config-panel__close',
                onClick: handleClose,
                disabled: isSubmitting,
                'aria-label': 'Close modal',
              },
              React.createElement(
                'svg',
                {
                  width: 20,
                  height: 20,
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 2,
                },
                [
                  React.createElement('line', {
                    key: 'l1',
                    x1: 18,
                    y1: 6,
                    x2: 6,
                    y2: 18,
                  }),
                  React.createElement('line', {
                    key: 'l2',
                    x1: 6,
                    y1: 6,
                    x2: 18,
                    y2: 18,
                  }),
                ],
              ),
            ),
          ],
        ),

        // Content
        React.createElement(
          'form',
          {
            key: 'content',
            className: 'remote-config-modal__content',
            onSubmit: handleSubmit,
          },
          [
            // Feature name
            React.createElement(
              'div',
              {
                key: 'name',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Feature Name *',
                ),
                React.createElement('input', {
                  key: 'input',
                  type: 'text',
                  className:
                    'remote-config-form-input' +
                    (errors.configuration_name
                      ? ' remote-config-form-input--error'
                      : ''),
                  placeholder: 'Enter feature name',
                  value: formData.configuration_name,
                  onChange: handleInputChange('configuration_name'),
                  disabled: isSubmitting,
                }),
                errors.configuration_name &&
                  React.createElement(
                    'div',
                    {
                      key: 'error',
                      className: 'remote-config-form-error',
                    },
                    errors.configuration_name,
                  ),
              ],
            ),

            // Description
            React.createElement(
              'div',
              {
                key: 'description',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Description *',
                ),
                React.createElement('input', {
                  key: 'input',
                  type: 'text',
                  className:
                    'remote-config-form-input' +
                    (errors.description
                      ? ' remote-config-form-input--error'
                      : ''),
                  placeholder: 'Enter configuration description',
                  value: formData.description,
                  onChange: handleInputChange('description'),
                  disabled: isSubmitting,
                }),
                errors.description &&
                  React.createElement(
                    'div',
                    {
                      key: 'error',
                      className: 'remote-config-form-error',
                    },
                    errors.description,
                  ),
              ],
            ),

            // Default value
            React.createElement(
              'div',
              {
                key: 'defaultValue',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Default Value',
                ),
                React.createElement(
                  'select',
                  {
                    key: 'select',
                    className: 'remote-config-form-select',
                    value: formData.default_value.toString(),
                    onChange: handleInputChange('default_value'),
                    disabled: isSubmitting,
                  },
                  VALUE_OPTIONS.map(function (option) {
                    return React.createElement(
                      'option',
                      {
                        key: option.value,
                        value: option.value,
                      },
                      option.label,
                    );
                  }),
                ),
              ],
            ),

            // Environments
            React.createElement(
              'div',
              {
                key: 'environments',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Affected Environments',
                ),
                React.createElement(MultiSelect, {
                  key: 'select',
                  options: CONFIGURATION_ENV_OPTIONS,
                  value: getMultiSelectValue(formData.environments),
                  onChange: handleMultiSelectChange('environments'),
                  labelledBy: 'Select environments',
                  disabled: isSubmitting,
                }),
              ],
            ),

            // Distributions
            React.createElement(
              'div',
              {
                key: 'distributions',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Affected Distributions',
                ),
                React.createElement(MultiSelect, {
                  key: 'select',
                  options: distributionOptions,
                  value: getMultiSelectValue(formData.distributions),
                  onChange: handleMultiSelectChange('distributions'),
                  labelledBy: 'Select distributions',
                  disabled: isSubmitting,
                }),
              ],
            ),

            // Farming units
            React.createElement(
              'div',
              {
                key: 'farmingUnits',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Affected Farming Units',
                ),
                React.createElement(MultiSelect, {
                  key: 'select',
                  options: farmingUnitOptions,
                  value: getMultiSelectValue(formData.farming_units),
                  onChange: handleMultiSelectChange('farming_units'),
                  labelledBy: 'Select farming units',
                  disabled: isSubmitting,
                }),
              ],
            ),

            // Users email
            React.createElement(
              'div',
              {
                key: 'users',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Affected Users',
                ),
                React.createElement('input', {
                  key: 'input',
                  type: 'text',
                  className: 'remote-config-form-input',
                  placeholder: 'Enter emails separated by comma',
                  value: formData.users_email,
                  onChange: handleInputChange('users_email'),
                  disabled: isSubmitting,
                }),
              ],
            ),

            // Extra data
            React.createElement(
              'div',
              {
                key: 'extraData',
                className: 'remote-config-form-group',
              },
              [
                React.createElement(
                  'label',
                  {
                    key: 'label',
                    className: 'remote-config-form-label',
                  },
                  'Extra Data (JSON)',
                ),
                React.createElement('input', {
                  key: 'input',
                  type: 'text',
                  className:
                    'remote-config-form-input' +
                    (errors.extra_data
                      ? ' remote-config-form-input--error'
                      : ''),
                  placeholder: '{"key": "value"}',
                  value: formData.extra_data,
                  onChange: handleInputChange('extra_data'),
                  disabled: isSubmitting,
                }),
                errors.extra_data &&
                  React.createElement(
                    'div',
                    {
                      key: 'error',
                      className: 'remote-config-form-error',
                    },
                    errors.extra_data,
                  ),
              ],
            ),
          ],
        ),

        // Footer
        React.createElement(
          'div',
          {
            key: 'footer',
            className: 'remote-config-modal__footer',
          },
          [
            React.createElement(
              'button',
              {
                key: 'cancel',
                type: 'button',
                className: 'remote-config-btn-secondary',
                onClick: handleClose,
                disabled: isSubmitting,
              },
              'Cancel',
            ),
            React.createElement(
              'button',
              {
                key: 'submit',
                type: 'button',
                className: 'remote-config-btn-primary',
                onClick: handleSubmit,
                disabled: isSubmitting,
              },
              isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create',
            ),
          ],
        ),
      ],
    ),
  );
};

module.exports = ConfigurationModal;
